/**
 * EmptyState — §7.14, in a table's place (mission 6 WP03 T023; FR-005,
 * NFR-007; US8.4; post-tasks P12, E16).
 *
 * A 48px Sky-burst-stroke icon in a 72px accent-50 circle, an `h3` title, a
 * navy-600 description, then the caller's action and any number of router
 * links, in order (sheet 10 callout 10 needs two). Every string is a prop.
 */
import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Link } from 'react-router-dom'

export interface EmptyStateProps {
  icon: LucideIcon
  title: ReactNode
  description: ReactNode
  action?: ReactNode
  links?: Array<{ to: string; label: string }>
}

export function EmptyState({ icon: Icon, title, description, action, links = [] }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <div
        data-empty-icon=""
        className="flex size-[72px] items-center justify-center rounded-full bg-[var(--color-accent-50)]"
      >
        <Icon aria-hidden strokeWidth={1.5} className="size-12 stroke-brand-sky" />
      </div>
      <h3 className="text-h3 text-navy-950">{title}</h3>
      <p className="max-w-[68ch] text-body text-navy-600">{description}</p>
      {action || links.length > 0 ? (
        <div className="mt-1 flex flex-wrap items-center justify-center gap-4">
          {action}
          {links.map((link) => (
            <Link
              key={`${link.to}|${link.label}`}
              to={link.to}
              className="text-body text-[var(--color-link)] underline-offset-4 hover:text-[var(--color-link-hover)] hover:underline"
            >
              {link.label}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  )
}
